// src/pages/offlineSales/terminal/OfflineSalesTerminal.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronDown, User, UserPlus, FileText, XCircle, Warehouse, Maximize, Minimize, Keyboard, RefreshCw, MonitorSmartphone, Store, X, CalendarClock } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAuth } from '../../../hooks/useAuth';
import { useAlert } from '../../../contexts/AlertContext';
import { usePermissions } from '../../../hooks/usePermissions';
import useOfflineSalesTerminal from './hooks/useOfflineSalesTerminal';
import useCustomerDisplaySync from './hooks/useCustomerDisplaySync';
import CartPanel from './components/CartPanel';
import ProductGrid from './components/ProductGrid';
import SessionGate from './components/SessionGate';
import CustomerSelectorModal from './components/CustomerSelectorModal';
import PaymentModal from './components/PaymentModal';
import OrderDiscountModal from './components/OrderDiscountModal';
import LoyaltyRedeemModal from './components/LoyaltyRedeemModal';
import HoldOrdersDrawer from './components/HoldOrdersDrawer';
import ReceiptModal from './components/ReceiptModal';
import HotkeysModal from './components/HotkeysModal';
import BackdateModal from './components/BackdateModal';
import ZReportModal from '../components/ZReportModal';
import CloseSessionModal from '../components/CloseSessionModal';
import Dropdown from '../../../components/ui/Dropdown/Dropdown';
import styles from './OfflineSalesTerminal.module.css';

const OfflineSalesTerminalContent = ({ onReload }) => {
  const { t } = useLocalization();
  const { user } = useAuth();
  const { error: showError } = useAlert();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const {
    shop, session, settings, paymentMethods, customer, warehouses, warehouseId, loading, submitting, smsEnabled,
    cartItems, orderDiscount, loyaltyPointsToRedeem, totals, completedOrder, holdOrders, storePickupOrder, saleDate, backdate,
    showPaymentModal, showDiscountModal, showLoyaltyModal,
    showCustomerModal, showHoldDrawer, showReceiptModal, showSessionGate,
    setCustomer, setBackdate, setOrderDiscount, setLoyaltyPointsToRedeem, setWarehouseId,
    setShowPaymentModal, setShowDiscountModal, setShowLoyaltyModal,
    setShowCustomerModal, setShowHoldDrawer, setShowReceiptModal,
    onSessionOpened, onSessionClosed,
    addToCart, updateCartItem, removeFromCart, clearCart,
    submitOrder, holdOrder, recallHoldOrder, removeStorePickupPayment,
  } = useOfflineSalesTerminal();

  const { openCustomerDisplay } = useCustomerDisplaySync({
    shop, customer, cartItems, totals, orderDiscount, completedOrder, showReceiptModal,
  });

  const [showSessionZReport, setShowSessionZReport] = useState(false);
  const [showCloseSession, setShowCloseSession] = useState(false);
  const [openCustomerCreate, setOpenCustomerCreate] = useState(false);
  const [showHotkeys, setShowHotkeys] = useState(false);
  const [showBackdate, setShowBackdate] = useState(false);
  const canBackdate = hasPermission('backdate_pos_order');
  // While backdating, the Z-Report shows the session the backdated sales go into
  const zReportSession = backdate ? { id: backdate.sessionId } : session;
  const [paymentInitialMethod, setPaymentInitialMethod] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));

  // Keep the icon in sync when full screen is left via Esc / F11
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Quick sale (Pay All): complete instantly as an exact cash payment of the grand total.
  // submitOrder clears the cart and opens the receipt modal on success.
  const handleQuickSale = () => {
    if (cartItems.length === 0 || submitting) return;
    // A store pickup order may already carry payments — settle it through the payment modal
    if (storePickupOrder) { setPaymentInitialMethod(null); setShowPaymentModal(true); return; }
    const methodKey = paymentMethods.some(m => m.key === 'cash')
      ? 'cash'
      : (paymentMethods[0]?.key || 'cash');
    const amount = totals.grandTotal;
    const detail = { paymentMethodKey: methodKey, amount };
    if (methodKey === 'cash') {
      detail.cashReceived = amount;
      detail.changeGiven = 0;
    }
    submitOrder(amount > 0 ? [detail] : [], 'completed');
  };

  // Cart and held orders live only in memory, so warn before a reload wipes them
  const handleReload = () => {
    if ((cartItems.length > 0 || holdOrders.length > 0) &&
        !window.confirm('Reloading will clear the current cart and held orders. Reload the terminal?')) {
      return;
    }
    onReload();
  };

  // Store pickup orders are saved as regular orders, which have no order-level discount or loyalty redemption
  const openDiscount = () => {
    if (storePickupOrder) { showError('Use line discounts for store pickup orders'); return; }
    setShowDiscountModal(true);
  };
  const openLoyalty = () => {
    if (storePickupOrder) { showError('Loyalty redemption is not available for store pickup orders'); return; }
    setShowLoyaltyModal(true);
  };

  // Drops the store pickup order from the terminal; nothing is saved to the order
  const cancelStorePickup = () => {
    clearCart();
    setCustomer(null);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    } else {
      document.documentElement.requestFullscreen?.().catch(() => {});
    }
  };

  // ── Global keyboard shortcuts (F1–F5) ──
  useEffect(() => {
    const onKey = (e) => {
      // Don't fire while typing in an input
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      // Don't fire when any modal/drawer is already open
      if (showPaymentModal || showDiscountModal || showLoyaltyModal ||
          showCustomerModal || showHoldDrawer || showReceiptModal || showHotkeys || showBackdate) return;

      switch (e.key) {
        case 'F1':
          e.preventDefault();
          if (cartItems.length > 0) { setPaymentInitialMethod('cash'); setShowPaymentModal(true); }
          break;
        case 'F2':
          e.preventDefault();
          holdOrder();
          break;
        case 'F3':
          e.preventDefault();
          clearCart();
          break;
        case 'F4':
          e.preventDefault();
          openDiscount();
          break;
        case 'F5':
          e.preventDefault();
          setShowCustomerModal(true);
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [
    showPaymentModal, showDiscountModal, showLoyaltyModal,
    showCustomerModal, showHoldDrawer, showReceiptModal, showHotkeys, showBackdate,
    cartItems.length, setShowPaymentModal, setShowDiscountModal,
    setShowCustomerModal, holdOrder, clearCart, storePickupOrder,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className={styles.loadingScreen}>
        <div className={styles.loadingSpinner} />
      </div>
    );
  }

  if (showSessionGate) {
    return (
      <SessionGate
        shopId={shop?.id}
        onSessionOpened={onSessionOpened}
      />
    );
  }

  return (
    <div className={styles.terminal}>
      {/* ── Top bar ── */}
      <div className={styles.topBar}>
        <span className={styles.shopName}>{shop?.name}</span>

        <button className={styles.exitBtn} onClick={() => navigate('/offline-sales/orders')} title={t('pos.exitTerminal')}>
          <ChevronLeft size={18} />
          <span>{t('pos.exitTerminal')}</span>
        </button>

        <div className={styles.topBarRight}>
          <span className={styles.cashierBadge}>
            <User size={14} />
            {user?.name}
          </span>

          {backdate ? (
            // Backdated sales go to that date's session — show its status, not the current session's
            <span
              className={`${styles.sessionStatus} ${backdate.sessionStatus === 'open' ? styles.sessionOpen : styles.sessionClosed}`}
              title={`${t('pos.backdatedTo', 'Backdated to')} ${backdate.date}`}
            >
              <span className={styles.sessionDot} />
              #{backdate.sessionId} · {backdate.sessionStatus === 'open' ? t('pos.sessionOpen') : t('pos.sessionClosedLabel', 'Closed')}
            </span>
          ) : (
            <span className={`${styles.sessionStatus} ${session ? styles.sessionOpen : styles.sessionClosed}`}>
              <span className={styles.sessionDot} />
              {session ? t('pos.sessionOpen') : t('pos.noSession')}
            </span>
          )}

          {zReportSession && (
            <div className={styles.sessionGroup}>
              <button
                className={styles.sessionActionBtn}
                onClick={() => setShowSessionZReport(true)}
                title={backdate ? `${t('pos.viewZReport')} #${backdate.sessionId}` : t('pos.viewZReport')}
              >
                <FileText size={16} />
                {t('pos.zReport')}
              </button>
              {/* Closing always applies to the cashier's current session, never a backdated one */}
              {session && (
                <button
                  className={`${styles.sessionActionBtn} ${styles.sessionCloseBtn}`}
                  onClick={() => setShowCloseSession(true)}
                  title={t('pos.closeSession')}
                >
                  <XCircle size={16} />
                  {t('pos.closeSession')}
                </button>
              )}
            </div>
          )}

          {holdOrders.length > 0 && (
            <button
              className={styles.holdBadge}
              onClick={() => setShowHoldDrawer(true)}
              title={t('pos.holdOrders')}
            >
              {t('pos.holdOrders')} ({holdOrders.length})
            </button>
          )}

          {canBackdate && (
            <button
              className={`${styles.fullscreenBtn} ${saleDate ? styles.backdateBtnActive : ''}`}
              onClick={() => setShowBackdate(true)}
              title={saleDate ? `${t('pos.backdatedTo', 'Backdated to')} ${saleDate}` : t('pos.saleDate', 'Sale Date')}
            >
              <CalendarClock size={16} />
              {saleDate && <span>{saleDate}</span>}
            </button>
          )}

          <button
            className={styles.fullscreenBtn}
            onClick={openCustomerDisplay}
            title="Open customer display"
          >
            <MonitorSmartphone size={16} />
          </button>

          <button
            className={styles.fullscreenBtn}
            onClick={handleReload}
            title="Reload terminal"
          >
            <RefreshCw size={16} />
          </button>

          <button
            className={styles.fullscreenBtn}
            onClick={() => setShowHotkeys(true)}
            title="Keyboard shortcuts"
          >
            <Keyboard size={16} />
          </button>

          <button
            className={styles.fullscreenBtn}
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit full screen' : 'Full screen'}
          >
            {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
          </button>
        </div>
      </div>

      {/* ── Main body ── */}
      <div className={styles.body}>
        <CartPanel
          topSection={
            <div className={styles.cartContext}>
              {storePickupOrder && (
                <div className={styles.storePickupBanner}>
                  <Store size={16} />
                  <span>Completing store pickup order <strong>#{storePickupOrder.orderNumber}</strong></span>
                  <button type="button" onClick={cancelStorePickup} title="Cancel store pickup completion">
                    <X size={16} />
                  </button>
                </div>
              )}
              <div className={styles.contextField}>
                <span className={styles.contextIcon}><Warehouse size={18} /></span>
                {warehouses.length > 1 ? (
                  <Dropdown
                    value={warehouseId || ''}
                    onChange={value => setWarehouseId(value ? parseInt(value) : null)}
                    options={warehouses.map(w => ({ value: w.id, label: w.name }))}
                    placeholder={t('pos.selectWarehouse')}
                    searchable
                    allowClear={false}
                    className={styles.contextDropdown}
                  />
                ) : (
                  <span className={styles.contextValue}>{warehouses[0]?.name || 'No warehouse assigned'}</span>
                )}
              </div>
              <div className={styles.contextRow}>
                <button
                  type="button"
                  className={styles.contextField}
                  onClick={() => { setOpenCustomerCreate(false); setShowCustomerModal(true); }}
                >
                  <span className={styles.contextIcon}><User size={18} /></span>
                  <span className={styles.contextValue}>{customer ? customer.name : t('pos.cashSales')}</span>
                  <kbd className={styles.kbdShortcut}>F5</kbd>
                  <ChevronDown size={16} className={styles.contextChevron} />
                </button>
                <button
                  type="button"
                  className={styles.contextAddBtn}
                  onClick={() => { setOpenCustomerCreate(true); setShowCustomerModal(true); }}
                  title="Add customer"
                >
                  <UserPlus size={18} />
                </button>
              </div>
            </div>
          }
          cartItems={cartItems}
          orderDiscount={orderDiscount}
          totals={totals}
          onUpdateItem={updateCartItem}
          onRemoveItem={removeFromCart}
          onOpenDiscount={openDiscount}
          onOpenLoyalty={openLoyalty}
          onHold={holdOrder}
          onClear={clearCart}
          onCharge={() => { setPaymentInitialMethod(null); setShowPaymentModal(true); }}
          onCash={() => { setPaymentInitialMethod('cash'); setShowPaymentModal(true); }}
          onQuickSale={handleQuickSale}
          submitting={submitting}
          settings={settings}
          customer={customer}
          canChangeUnitPrice={hasPermission('change_pos_unit_price')}
        />

        <ProductGrid
          shopId={shop?.id}
          onAddToCart={addToCart}
        />
      </div>

      {/* ── Modals ── */}
      {showHotkeys && <HotkeysModal onClose={() => setShowHotkeys(false)} />}

      {showBackdate && (
        <BackdateModal
          shopId={shop?.id}
          current={backdate}
          onApply={(value) => { setBackdate(value); setShowBackdate(false); }}
          onClose={() => setShowBackdate(false)}
        />
      )}

      {showCustomerModal && (
        <CustomerSelectorModal
          current={customer}
          initialCreate={openCustomerCreate}
          onSelect={(c) => { setCustomer(c); setShowCustomerModal(false); setOpenCustomerCreate(false); }}
          onClose={() => { setShowCustomerModal(false); setOpenCustomerCreate(false); }}
        />
      )}

      {showPaymentModal && (
        <PaymentModal
          grandTotal={totals.grandTotal}
          initialMethodKey={paymentInitialMethod}
          paymentMethods={paymentMethods}
          onConfirm={(paymentDetails, status) => submitOrder(paymentDetails, status)}
          onClose={() => setShowPaymentModal(false)}
          submitting={submitting}
          existingPayments={storePickupOrder?.paymentRecords || []}
          onRemoveExistingPayment={removeStorePickupPayment}
          allowCredit={!storePickupOrder}
        />
      )}

      {showDiscountModal && (
        <OrderDiscountModal
          subtotal={totals.subtotal}
          currentDiscount={orderDiscount}
          onApply={setOrderDiscount}
          onClose={() => setShowDiscountModal(false)}
        />
      )}

      {showLoyaltyModal && (
        <LoyaltyRedeemModal
          customer={customer}
          settings={settings}
          grandTotal={totals.grandTotal}
          currentPoints={loyaltyPointsToRedeem}
          onApply={setLoyaltyPointsToRedeem}
          onClose={() => setShowLoyaltyModal(false)}
        />
      )}

      {showHoldDrawer && (
        <HoldOrdersDrawer
          holdOrders={holdOrders}
          onRecall={recallHoldOrder}
          onClose={() => setShowHoldDrawer(false)}
        />
      )}

      {showReceiptModal && (
        <ReceiptModal
          order={completedOrder}
          shop={shop}
          settings={settings}
          smsEnabled={smsEnabled}
          onClose={() => setShowReceiptModal(false)}
          onNewSale={() => { clearCart(); setShowReceiptModal(false); }}
        />
      )}

      {showSessionZReport && zReportSession && (
        <ZReportModal
          key={zReportSession.id}
          session={zReportSession}
          onClose={() => setShowSessionZReport(false)}
        />
      )}

      {showCloseSession && session && (
        <CloseSessionModal
          session={session}
          onClosed={(closedSession) => {
            setShowCloseSession(false);
            onSessionClosed(closedSession);
          }}
          onClose={() => setShowCloseSession(false)}
        />
      )}
    </div>
  );
};

// Soft reload: bumping the key remounts the whole terminal, re-running every data load,
// without a browser reload — so full-screen mode is kept
const OfflineSalesTerminal = () => {
  const [reloadKey, setReloadKey] = useState(0);
  return (
    <OfflineSalesTerminalContent
      key={reloadKey}
      onReload={() => setReloadKey(k => k + 1)}
    />
  );
};

export default OfflineSalesTerminal;
